import { siteState } from '@/modules/siteState/siteStateGetter';
import { isOverNDiff } from '@/modules/utils/commonUtils';

const props = {
  click: {
    classNameToggle: 'js-classToggle',
    classNameToggleIs: 'js-classToggleIs',
    classNameAdd: 'js-classAdd',
    classNameRemove: 'js-classRemove',
    classNameRemoveIs: 'js-classRemoveIs',
  },

  hover: {
    className: 'js-classHover',
  },

  excludeIsClassList: ['is-anime'] as string[],

  raf: {
    className: 'js-classScroll',

    isClassNameUp: 'is-scrollUp',
    isClassNameDown: 'is-scrollDown',
  },
  observer: {
    className: 'js-classObserver',
    rootMargin: '-50% 0%',
  },
} as const;

const state = {
  scroll: {
    els: [] as HTMLElement[],
    lastScrollY: window.scrollY - 10,
    rafId: null as number | null,
  },

  observer: new Map<HTMLElement, IntersectionObserver>(),

  click: {
    handler: undefined as ((e: MouseEvent) => void) | undefined,
  },

  hover: {
    els: Array.from(document.querySelectorAll(`.${props.hover.className}`)),
  },

  mouseenter: {
    handler: undefined as ((e: MouseEvent) => void) | undefined,
  },

  mouseleave: {
    handler: undefined as ((e: MouseEvent) => void) | undefined,
  },
};

export const classManager = {
  init() {
    this.startRaf();
    this.applyObserver();

    this.click();

    if (siteState.isHover) {
      this.mouseenter();
      this.mouseleave();
    }
  },

  reset() {
    this.stopRaf();
    this.cleanupObserver();

    this.removeClick();
    this.removeMouseenter();
    this.removeMouseleave();
  },

  startRaf() {
    this.stopRaf(); // 念のため二重起動防止
    state.scroll.els = Array.from(document.querySelectorAll(`.${props.raf.className}`)) as HTMLElement[];

    // ページ読み込み直後にも1回チェック
    this.runRaf();

    if (state.scroll.els.length > 0) {
      state.scroll.rafId = requestAnimationFrame(() => this.runRaf());
    }
  },
  runRaf() {
    const currentScrollY = siteState.scrollY;

    if (isOverNDiff(currentScrollY, state.scroll.lastScrollY)) {
      state.scroll.els.forEach((el) => {
        const result = this.getDataAttr(el);
        if (!result) return;

        const { className, threshold, thresholdBottom } = result;

        if (threshold !== undefined) {
          const ifStmt = thresholdBottom //
            ? currentScrollY + siteState.winHeight >= threshold
            : currentScrollY >= threshold;

          if (ifStmt && !el.classList.contains(className)) {
            el.classList.add(className);
          } else if (!ifStmt && el.classList.contains(className)) {
            el.classList.remove(className);
          }
        }
      });

      // console.log(siteState.scrollDir);

      if (
        siteState.scrollDir === 'up' && //
        !document.documentElement.classList.contains(props.raf.isClassNameUp)
      ) {
        document.documentElement.classList.remove(props.raf.isClassNameDown);
        document.documentElement.classList.add(props.raf.isClassNameUp);
      } else if (
        siteState.scrollDir === 'down' && //
        !document.documentElement.classList.contains(props.raf.isClassNameDown)
      ) {
        document.documentElement.classList.remove(props.raf.isClassNameUp);
        document.documentElement.classList.add(props.raf.isClassNameDown);
      }

      state.scroll.lastScrollY = currentScrollY;
    }

    state.scroll.rafId = requestAnimationFrame(() => this.runRaf());
  },
  stopRaf() {
    if (state.scroll.rafId !== null) {
      cancelAnimationFrame(state.scroll.rafId);
      state.scroll.rafId = null;
    }
  },

  applyObserver() {
    this.cleanupObserver();

    const observerEls = Array.from(document.querySelectorAll(`.${props.observer.className}`)) as HTMLElement[];

    observerEls.forEach((observerEl) => {
      const observer = this.createObserver(observerEl);
      observer.observe(observerEl);
      state.observer.set(observerEl, observer); // Mapに保存
    });
  },
  createObserver(triggerEl: HTMLElement) {
    return new IntersectionObserver(
      (entries, _) => {
        entries.forEach((entry) => {
          const triggerEl = entry.target as HTMLElement;
          const result = this.getDataAttr(triggerEl);
          if (!result) return;

          const { className, targetEl } = result;

          if (targetEl) {
            if (entry.isIntersecting) {
              targetEl.classList.add(className);
            } else {
              targetEl.classList.remove(className);
            }
          }
        });
      },
      {
        rootMargin: this.getRootMargin(triggerEl),
        threshold: 0,
      },
    );
  },
  getRootMargin(el: HTMLElement): string {
    return el.dataset.rootMargin || props.observer.rootMargin;
  },
  cleanupObserver() {
    state.observer.forEach((observer) => observer.disconnect());
    state.observer.clear();
  },

  click() {
    if (state.click.handler) return;

    state.click.handler = (e: MouseEvent) => {
      // ### RUN ###
      this.clickToggleHandler(e, props.click.classNameToggle);
      this.clickToggleIsHandler(e, props.click.classNameToggleIs);
      this.clickAddHandler(e, props.click.classNameAdd);
      this.clickRemoveHandler(e, props.click.classNameRemove);
      this.clickRemoveIsHandler(e, props.click.classNameRemoveIs);
      // ### RUN END ###
    };

    document.addEventListener('click', state.click.handler);
  },
  clickToggleHandler(e: MouseEvent, classNameTrigger: string) {
    const result = this.getTriggerDataAttr(e, classNameTrigger);
    if (!result) return;

    const { className, targetEl } = result;

    if (targetEl) {
      this.toggleClass(targetEl, className);
    }
  },
  clickToggleIsHandler(e: MouseEvent, classNameTrigger: string) {
    const result = this.getTriggerDataAttr(e, classNameTrigger);
    if (!result) return;

    const { className, targetEl } = result;

    if (targetEl) {
      this.toggleIsClass(targetEl, className);
    }
  },
  clickAddHandler(e: MouseEvent, classNameTrigger: string) {
    const result = this.getTriggerDataAttr(e, classNameTrigger);
    if (!result) return;

    const { className, targetEl } = result;

    if (targetEl) {
      this.addClass(targetEl, className);
    }
  },
  clickRemoveHandler(e: MouseEvent, classNameTrigger: string) {
    const result = this.getTriggerDataAttr(e, classNameTrigger);
    if (!result) return;

    const { className, targetEl } = result;

    if (targetEl) {
      this.removeClass(targetEl, className);
    }
  },
  clickRemoveIsHandler(e: MouseEvent, classNameTrigger: string) {
    const result = this.getTriggerDataAttr(e, classNameTrigger);
    if (!result) return;

    const { className, targetEl } = result;

    if (targetEl) {
      const originEl = e.target as HTMLElement;
      const removeClassList =
        originEl.hasAttribute('data-class') && originEl.dataset.class //
          ? originEl.dataset.class
              .split(',')
              .map((str) => str.trim())
              .filter((str) => str !== '')
          : [];
      this.removeIsClass(targetEl, removeClassList);
    }
  },
  removeClick() {
    if (!state.click.handler) return;

    document.removeEventListener('click', state.click.handler);
    state.click.handler = undefined;
  },

  mouseenter() {
    if (state.mouseenter.handler) return;

    state.mouseenter.handler = (e: MouseEvent) => {
      const targetEl = e.target;
      const related = e.relatedTarget as Node | null;

      // targetEl が HTMLElement じゃなければ無視
      if (!(targetEl instanceof HTMLElement)) return;

      // targetEl が親要素の1つであるかチェック
      const parent = state.hover.els.find((el) => el === targetEl);
      if (!parent) return;

      // 子要素から入った場合は無視
      if (related && parent.contains(related)) return;

      // ### RUN ###
      this.mouseenterHandler(e, props.hover.className);
      // ### RUN END ###
    };

    document.addEventListener('mouseenter', state.mouseenter.handler, true);
  },
  mouseenterHandler(e: MouseEvent, classNameTrigger: string) {
    const result = this.getHoverDataAttr(e, classNameTrigger);
    if (!result) return;

    const { className, targetEl } = result;

    if (targetEl) {
      // this.addClass(targetEl, className);
      this.toggleIsClass(targetEl, className);
    }
  },
  removeMouseenter() {
    if (!state.mouseenter.handler) return;

    document.removeEventListener('mouseenter', state.mouseenter.handler);
    state.mouseenter.handler = undefined;
  },

  mouseleave() {
    if (state.mouseleave.handler) return;

    state.mouseleave.handler = (e: MouseEvent) => {
      const targetEl = e.target;
      const related = e.relatedTarget as Node | null;

      if (!(targetEl instanceof HTMLElement)) return;

      // targetEl が親要素の1つであるかチェック
      const parent = state.hover.els.find((el) => el === targetEl);
      if (!parent) return;

      // 子要素から出てきた場合は無視
      if (related && parent.contains(related)) return;

      // ### RUN ###
      this.mouseleaveHandler(e, props.hover.className);
      // ### RUN END ###
    };

    document.addEventListener('mouseleave', state.mouseleave.handler, true);
  },
  mouseleaveHandler(e: MouseEvent, classNameTrigger: string) {
    const result = this.getHoverDataAttr(e, classNameTrigger);
    if (!result) return;

    const { className, targetEl } = result;

    if (targetEl) {
      // 🔒 特定の data 属性がある場合はクラス削除を無効化
      if (targetEl.hasAttribute('data-hover-lock')) {
        return;
      }

      this.removeClass(targetEl, className);
    }
  },
  removeMouseleave() {
    if (!state.mouseleave.handler) return;

    document.removeEventListener('mouseleave', state.mouseleave.handler);
    state.mouseleave.handler = undefined;
  },

  getDataAttr(el: HTMLElement): { className: string; targetEl?: HTMLElement; threshold?: number; thresholdBottom?: boolean } | undefined {
    const className = el.dataset.class;
    if (!className) {
      console.warn('[classManager] data-class 属性が空、または未定義です', el);
      return;
    }

    let targetEl: HTMLElement | undefined = undefined;
    if (el.hasAttribute('data-target')) {
      const targetSel = el.dataset.target;
      if (!targetSel) {
        console.warn('[classManager] data-target 属性が空です', el);
        return;
      }

      const queried = document.querySelector<HTMLElement>(targetSel);
      if (!queried) {
        console.warn(`[classManager] 指定されたセレクタ "${targetSel}" に該当する要素が見つかりません`, el);
        return;
      }

      targetEl = queried;
    } else {
      targetEl = document.documentElement;
    }

    let threshold: number | undefined = undefined;
    if (el.hasAttribute('data-threshold')) {
      const thresholdVal = el.dataset.threshold;
      if (!thresholdVal) {
        console.warn('[classManager] data-threshold 属性が空です', el);
        return;
      }

      if (thresholdVal.endsWith('%')) {
        // %指定の場合は画面高さに対する割合で計算
        const percent = Number(thresholdVal.slice(0, -1));
        if (isNaN(percent)) {
          console.warn(`[classManager] data-threshold の値 "${thresholdVal}" は無効なパーセンテージです`, el);
          return;
        }
        threshold = (percent / 100) * window.innerHeight;
      } else {
        // 数字だけの場合は px として扱う
        const thresholdNum = Number(thresholdVal);
        if (isNaN(thresholdNum)) {
          console.warn(`[classManager] data-threshold の値 "${thresholdVal}" は数値ではありません`, el);
          return;
        }
        threshold = thresholdNum;
      }
    }

    if (targetEl) {
      const rect = targetEl.getBoundingClientRect();
      threshold = (threshold ?? 0) + rect.top + window.scrollY;
    }

    const thresholdBottom = el.hasAttribute('data-threshold-bottom');

    return { className, targetEl, threshold, thresholdBottom };
  },

  getTriggerDataAttr(e: MouseEvent, classNameTrigger: string) {
    const originEl = e.target;
    if (!(originEl instanceof HTMLElement)) return;

    const triggerEl = originEl.closest(`.${classNameTrigger}`);
    if (!(triggerEl instanceof HTMLElement)) return;

    return this.getDataAttr(triggerEl);
  },

  getHoverDataAttr(e: MouseEvent, classNameTrigger: string) {
    const originEl = e.target;
    if (!(originEl instanceof HTMLElement)) return;

    const hoverEl = originEl.closest(`.${classNameTrigger}`);
    if (!(hoverEl instanceof HTMLElement)) return;

    return this.getDataAttr(hoverEl);
  },

  toggleClass(targetEl: HTMLElement, className: string) {
    targetEl.classList.toggle(className);
  },
  toggleIsClass(targetEl: HTMLElement, className: string) {
    const cleanName = className.startsWith('is-') ? className.slice(3) : className;
    const isClass = `is-${cleanName}`;

    // すでに同じクラスが付いているなら何もしない
    if (targetEl.classList.contains(isClass)) return;

    this.removeIsClass(targetEl);
    this.addClass(targetEl, isClass);
  },

  addClass(targetEl: HTMLElement, className: string) {
    if (!targetEl.classList.contains(className)) {
      targetEl.classList.add(className);
    }
  },

  removeClass(targetEl: HTMLElement, className: string) {
    if (targetEl.classList.contains(className)) {
      targetEl.classList.remove(className);
    }
  },
  removeIsClass(el: HTMLElement, removeClassList: string[] = []) {
    el.classList.forEach((className) => {
      if (removeClassList.length > 0) {
        if (removeClassList.includes(className)) {
          el.classList.remove(className);
        }
      } else if (
        //
        className.startsWith('is-') &&
        !props.excludeIsClassList.includes(className)
      ) {
        el.classList.remove(className);
      }
    });
  },
};
